"use client";

import Translations from "@translate/components/EmailLogin/steps/CodeVerifyStepPopup";
import React, { useEffect, useMemo, useRef } from "react";
import { cn } from "utils";
import { I18nUtil } from "@utils/i18n";
import type { CodeVerifyStepProps } from "../types";
import { formatEmailForDisplay } from "../shared/formatEmailForDisplay";
import { CodeInputGroup, focusFirstCodeInput } from "../shared/CodeInputGroup";
import { ResendCodeRow } from "../shared/ResendCodeRow";
import { VerifyCodeButton } from "../shared/VerifyCodeButton";

const I18n = I18nUtil.createScoped(Translations);
const CODE_LENGTH = 6;

export function CodeVerifyStepPopup({
  email,
  code,
  onCodeChange,
  onVerify,
  verifying = false,
  codeError = false,
  resendCountdown,
  onResend,
  className,
  autoSubmitEnabled = true,
}: CodeVerifyStepProps) {
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const prevCodeErrorRef = useRef<boolean>(codeError);
  const displayEmail = useMemo(() => formatEmailForDisplay(email), [email]);
  const titleEmail = useMemo(() => email.trim().toLowerCase(), [email]);
  const fullCode = code.join("");
  const isCodeComplete = fullCode.length === CODE_LENGTH;
  const canVerify = isCodeComplete && !verifying;

  useEffect(() => {
    focusFirstCodeInput(inputRefs, "immediate");
  }, []);

  useEffect(() => {
    const prev = prevCodeErrorRef.current;
    prevCodeErrorRef.current = codeError;
    if (!codeError || prev) {
      return;
    }
    focusFirstCodeInput(inputRefs, "immediate");
  }, [codeError]);

  useEffect(() => {
    if (!isCodeComplete || verifying || autoSubmitEnabled === false) {
      return;
    }
    onVerify();
  }, [isCodeComplete, verifying, autoSubmitEnabled, onVerify]);

  return (
    <div className={cn("flex w-full flex-col gap-6", className)}>
      <div className="flex min-w-0 flex-col gap-3">
        <h2
          aria-label="Code Verify Step h2"
          data-openreplay-label="login_codeverifystep_h2"
          id="sem-code-verify-step-h2"
          className="headline_regular_l text-content_primary"
        >
          {I18n.t("Check your email")}
        </h2>
        <p
          className="body_m min-w-0 overflow-hidden text-ellipsis whitespace-nowrap text-content_secondary"
          title={titleEmail}
        >
          {I18n.t("We sent a 6-digit code to")}
          <span className="body_medium_m ml-1 text-content_primary">
            {displayEmail}
          </span>
        </p>
      </div>
      <div className="flex flex-col">
        <CodeInputGroup
          variant="popup"
          code={code}
          onCodeChange={onCodeChange}
          codeError={codeError}
          inputRefs={inputRefs}
        />
        {codeError && (
          <p className="body_s mt-2 text-content_accent2_primary" role="alert">
            {I18n.t("Invalid verification code")}
          </p>
        )}
        <ResendCodeRow
          resendCountdown={resendCountdown}
          onResend={onResend}
          codeError={codeError}
          className="mt-4"
        />
        <VerifyCodeButton
          variant="popup"
          canVerify={canVerify}
          verifying={verifying}
          onVerify={onVerify}
        />
      </div>
    </div>
  );
}
